import * as _ from 'underscore';
import { List } from './list';
import { Column } from './column';
import { FilterColumnOptions, FilterOptions, TableHeaderActionOptions, TableOptions } from '../interfaces/options';
import { FilterModal } from './modal';
import { DefaultTableOptions } from '../const/defaultOptions';
import { Icons, ID } from '../const';
import { KeyCode, EditableType, PropertyType, FilterOperator, StringComparer } from '../enums';
import { Localization } from '../compile';

export class Table<T> extends List<T> {
    public isFilterOn: KnockoutComputed<boolean>;

    protected properties: Column<T>[];
    public options: TableOptions<T>;

    public clickDeleteSelected: () => void;
    private clickNew: (...params: any[]) => void;
    private clickFilter: () => void;
    private clickSave: () => void;
    private clickCancel: () => void;
    public filterModal: FilterModal;
    private filterableColumns: FilterColumnOptions[];
    private isDeleteVisible: KnockoutObservable<boolean>;
    private isNewVisible: KnockoutObservable<boolean>;
    private isFilterVisible: KnockoutObservable<boolean>;
    private isSaveVisible: boolean;
    private isSaveActive: KnockoutComputed<boolean>;
    private isDeleteActive: KnockoutComputed<boolean>;
    private isNewActive: KnockoutComputed<boolean>;
    private isFilterActive: KnockoutComputed<boolean>;
    private customTableActions: TableHeaderActionOptions[];
    private clickInlineEdit: (item: T) => void;
    private keyPress: (column: Column<T>, e: JQueryKeyEventObject) => void;
    private filterCountLabel: KnockoutComputed<string>;
    private filterCount: KnockoutObservable<number>;
    private clickInvalid: () => void;
    private invalidCount: KnockoutObservable<number>;
    private invalidCountLabel: KnockoutComputed<string>;
    private isInvalidVisible: KnockoutObservable<boolean>;
    private isInvalidActive: KnockoutComputed<boolean>;

    public isSearchActive: KnockoutComputed<boolean>;
    public isSearchVisible: KnockoutObservable<boolean>;
    public searchInput: KnockoutObservable<string>;

    public searchInputSubscription: KnockoutSubscription;

    constructor(options?: TableOptions<T>) {

        options = $.extend(true, {}, DefaultTableOptions, options || {});
        super(options);

        //overwrite
        var _itemsCountInfo = this.itemsCountInfo;
        this.itemsCountInfo = ko.pureComputed(() => {
            if (this.isFilterOn()) {
                return Localization.Items.concat(
                    ': ',
                    this.dataSource.filteringDataMap.map().length.toString(),
                    '/',
                    this.dataSource.length().toString()
                );
            } else {
                return _itemsCountInfo();
            }
        });

        this.searchInput = ko.observable<string>("");

        this.filterableColumns = null;

        //custom actions
        this.customTableActions = [];
        if (
            this.options.header &&
            _.isArray(this.options.header.customActions) &&
            this.options.header.customActions.length > 0
        ) {
            this.customTableActions = this.options.header.customActions.map(customAction => {
                return _.defaults(customAction, <TableHeaderActionOptions>{
                    icon: '',
                    isActive: true,
                    label: Localization.Actions,
                    onClick: () => { }
                });
            });
        }

        //Visible
        this.isDeleteVisible = !(_.isEmpty(options.visible) || _.isUndefined(options.visible.delete))
            ? _.isBoolean(options.visible.delete)
                ? ko.observable<boolean>(<boolean>options.visible.delete)
                : <KnockoutObservable<boolean>>options.visible.delete
            : ko.observable<boolean>(true);

        this.isNewVisible = !(_.isEmpty(options.visible) || _.isUndefined(options.visible.new))
            ? _.isBoolean(options.visible.new)
                ? ko.observable<boolean>(<boolean>options.visible.new)
                : <KnockoutObservable<boolean>>options.visible.new
            : ko.observable<boolean>(true);

        this.isFilterVisible = !(_.isEmpty(options.visible) || _.isUndefined(options.visible.filter))
            ? _.isBoolean(options.visible.filter)
                ? ko.observable<boolean>(<boolean>options.visible.filter)
                : <KnockoutObservable<boolean>>options.visible.filter
            : ko.observable<boolean>(true);

        this.isSearchVisible = options.textFilterColumn ? ko.observable<boolean>(true) : ko.observable<boolean>(false);

        this.isInvalidVisible = !(_.isEmpty(options.visible) || _.isUndefined(options.visible.invalid))
            ? _.isBoolean(options.visible.invalid)
                ? ko.observable<boolean>(<boolean>options.visible.invalid)
                : <KnockoutObservable<boolean>>options.visible.invalid
            : ko.observable<boolean>(true);

        this.isInvalidVisible(this.isInvalidVisible() && this.hasInvalidColumn());
        this.isSaveVisible = this.editable;

        //Active
        this.isDeleteActive = ko.computed(() => {
            return this.selectionCount() > 0;
        });

        this.isNewActive = ko.computed(() => {
            return true;
        });

        this.isFilterActive = ko.computed(() => {
            return true;
        });

        this.isSearchActive = ko.computed(() => {
            return true;
        });

        this.isSaveActive = ko.pureComputed(() => {
            return this.editingCount() > 0;
        });

        this.isInvalidActive = ko.pureComputed(() => {
            return this.invalidCount() > 0;
        });

        this.filterModal = null;
        if (this.getFilterableColumns().length > 0) {
            this.filterModal = new FilterModal(this.getFilterableColumns());
            this.filterModal.Changed.subscribe(filterSettings => {
                if (filterSettings) {
                    this.filterCount(filterSettings.length);
                    this.dataSource.filter([...filterSettings, {
                        key: options.textFilterColumn,
                        operator: FilterOperator.LIKE,
                        params: [this.searchInput()],
                        type: PropertyType.STRING,
                        stringComparer: StringComparer.CaseInSensitive
                    }]);
                } else {
                    this.filterCount(0);
                }
            });
        }

        this.searchInputSubscription = this.searchInput.subscribe(val => {
            let filterList: FilterOptions[] = [];
            if(this.filterModal)
                filterList = [...this.filterModal.getFilterSettings()];

            this.dataSource.filter([...filterList, {
                key: options.textFilterColumn,
                operator: FilterOperator.LIKE,
                params: [val],
                type: PropertyType.STRING,
                stringComparer: StringComparer.CaseInSensitive
            }]);
        });

        this.isFilterOn = ko.pureComputed(() => this.dataSource.filteringDataMap.isFilterOn());

        //Clicks
        this.clickDeleteSelected = () => {
            this.deleteSelected();
        };

        this.clickNew = (...params: any[]) => {
            this.new.apply(this, params);
        };

        this.clickFilter = () => {
            this.filter();
        };

        this.clickInvalid = () => {
            this.toggleInvalidFilter();
        };

        ////Inline Edit

        this.clickCancel = () => {
            this.cancel();
        };

        this.clickSave = () => {
            this.save();
        };

        this.keyPress = (column: Column<T>, e: JQueryKeyEventObject) => {
            switch (e.keyCode) {
                case KeyCode.ENTER:
                    this.clickSave();
                    break;
                case KeyCode.ESCAPE:
                    this.cancel();
            }
        };

        this.clickInlineEdit = (item: T) => {
            this.toggleEditing(item);
        };

        if (this.editable) {
            var commandColumn: Column<T> = _.find(
                this.properties,
                (column: Column<T>) => column.key == ID.Commands
            );
            if (commandColumn) {
                var isNotEditing = (item: T) => {
                    return !this.isEditing(item);
                };

                commandColumn.addEditableDependency(isNotEditing);

                if (this.editableType !== EditableType.ONLYNEW) {
                    commandColumn.addCommand({
                        label: Localization.Edit,
                        css: Icons.Edit,
                        editable: isNotEditing,
                        command: (item: T, table: Table<any>) => {
                            this.toggleEditing(item);
                        }
                    });
                }
            }
        }

        //labels
        this.filterCount = ko.observable(0);
        this.filterCountLabel = ko.pureComputed<string>(() => {
            var extension = '';
            var length: number = this.filterCount();
            if (length > 0) {
                extension = `(${length})`;
            }
            return extension;
        });

        this.invalidCount = ko.observable(0);
        this.invalidCountLabel = ko.pureComputed<string>(() => {
            var extension = '';
            var length: number = this.invalidCount();
            if (length > 0) {
                extension = `(${length})`;
            }
            return extension;
        });
        this.dataSource.onDataChanged.subscribe(_data => {
            let preInvalidCount = this.invalidCount();
            let postInvalidCount = _.foldl(
                _data.data,
                (prev: number, curr: T): number => {
                    let isCurrInvalid: number = curr[ID.IsValid] !== undefined && !curr[ID.IsValid] ? 1 : 0;
                    return prev + isCurrInvalid;
                },
                0
            );
            this.invalidCount(postInvalidCount);

            if (preInvalidCount > 0 && postInvalidCount == 0) {
                this.removeInvalidFilter();
            }
        });
    }

    private getFilterableColumns(): FilterColumnOptions[] {
        if (this.filterableColumns) {
            return this.filterableColumns;
        }
        return (this.filterableColumns = this.properties.filter(property => property.isFilterable).map(
            property =>
                <FilterColumnOptions>{
                    id: property.key,
                    label: property.label,
                    type: property.type
                }
        ));
    }

    private hasInvalidColumn(): boolean {
        return _.some(
            this.getFilterableColumns(),
            filterableColumn => filterableColumn.id == ID.IsValid
        );
    }

    public new(...params: any[]): void {
        this.dataSource.new.apply(this.dataSource, params);
    }

    public edit(item: T): void {
        if (_.isFunction(this.options.edit)) {
            <Promise<any>>this.options.edit.call(this, item);
        }
    }

    public filter(): void {
        if (this.filterModal) {
            this.filterModal.open();
        }
    }

    public toggleInvalidFilter(): void {
        if (this.filterModal && this.hasInvalidColumn() && this.isInvalidActive()) {
            if (!this.removeInvalidFilter()) {
                let invalidFilterOptions: FilterColumnOptions = {
                    id: ID.IsValid,
                    label: Localization.Valid,
                    type: PropertyType.BOOLEAN,
                    operator: FilterOperator.EQUALS,
                    value: 'false'
                };
                this.filterModal.addFilter(invalidFilterOptions, true);
            }
        }
    }

    public removeInvalidFilter(): boolean {
        if (this.filterModal.isActiveFilterById(ID.IsValid)) {
            this.filterModal.removeFilterById(ID.IsValid, true);
            return true;
        }
        return false;
    }
}